import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Valoracion } from '../components/Valoracion';
import { PedidoMaterial } from '../components/PedidoMaterial';
import { DatosVisita } from '../components/DatosVisita';
import { CabeceraPagina } from '../components/Armazon';
import { BotonHistorial, CintaEstado, PanelHistorial } from '../components/Historial';
import { Pase, turnoDe } from '../components/Pase';
import {
  SelectorFranja,
  franjaATexto,
  type Franja as FranjaSel,
} from '../components/SelectorFranja';
import {
  Aviso,
  Boton,
  Campo,
  Cargando,
  Dialogo,
  Lectura,
  Pildora,
  PildoraLibre,
  Tarjeta,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { autoriaDe, fechaHora, franja, tipoVisita, lugarVisita } from '../lib/formato';
import { useAvisos, useDatos, useSesion } from '../lib/sesion';
import type { Franja, PlanCocina, Solicitud } from '../lib/tipos';

type Ficha = Solicitud & {
  parent: { id: string; publicCode: string } | null;
  children: Array<{ id: string; publicCode: string; status: string }>;
};

export function SolicitudDetalle() {
  const { id = '' } = useParams();
  const ubicacion = useLocation();
  const volver = typeof ubicacion.state?.volver === 'string' && ubicacion.state.volver.startsWith('/agenda?') ? ubicacion.state.volver : '/solicitudes';
  const navegar = useNavigate();
  const { es, usuario } = useSesion();
  const { avisar } = useAvisos();

  const { datos, cargando, error, recargar } = useDatos(
    (s) => api.get<Ficha>(`/requests/${id}`, s),
    [id],
  );

  const [dialogo, setDialogo] = useState<null | 'autorizar' | 'rechazar' | 'cancelar'>(null);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  if (cargando) return <Cargando texto="Abriendo la solicitud…" />;
  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return null;

  const s = datos;
  const p = s.permissions ?? {};
  // M-04 · quién dejó la solicitud en su estado actual, según el histórico.
  const autoria = autoriaDe(s.timeline, s.status);

  async function ejecutar(fn: () => Promise<unknown>, exito: string) {
    setFallo(null);
    try {
      await fn();
      avisar(exito, 'hecho');
      setDialogo(null);
      recargar();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido completar la operación.');
    }
  }

  async function crearNuevaPropuesta() {
    setFallo(null);
    try {
      const nueva = await api.post<Solicitud>(`/requests/${s.id}/new-proposal`);
      if (usuario?.id === s.owner.id) {
        avisar('Nueva propuesta creada a partir de esta solicitud.', 'hecho');
        navegar(`/solicitudes/${nueva.id}`);
      } else {
        recargar();
        avisar(`Nueva propuesta ${nueva.publicCode} creada; queda pendiente del Comercial`, 'hecho');
      }
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido completar la operación.');
    }
  }

  return (
    <>
      <CabeceraPagina
        titulo={s.client.commercialName}
        descripcion={`${turnoDe(s.status, s.kitchenPlanStatus, autoria)} · ${
          [s.client.city, s.client.province].filter(Boolean).join(', ') || 'Sin localidad'
        }`}
        acciones={
          <>
            <Boton variante="secundario" onClick={() => navegar(volver)}>
              Volver
            </Boton>
            <BotonHistorial n={s.timeline.length} onAbrir={() => setHistorialAbierto(true)} />
            {p.newProposal && (
              <Boton
                variante="secundario"
                onClick={crearNuevaPropuesta}
              >
                Crear nueva propuesta
              </Boton>
            )}
            {p.reject&&<Boton variante="riesgo" onClick={()=>setDialogo('rechazar')}>Rechazar administrativamente</Boton>}
            {p.cancel && (
              <Boton variante="riesgo" onClick={() => setDialogo('cancelar')}>
                Cancelar visita
              </Boton>
            )}
          </>
        }
      />

      <div className="pila">
        <div className="fila" style={{ justifyContent: 'space-between' }}>
          <div className="fila">
            <span className="codigo">{s.publicCode}</span>
            <Pildora estado={s.status} />
            {Boolean(s.rescheduleCount)&&<PildoraLibre texto="Reprogramada" tono="espera" />}
            {s.status === 'AUTHORIZED' && (
              <PildoraLibre
                texto={s.kitchenPlanStatus === 'CONFIRMED' ? 'Cocina confirmada' : 'Cocina pendiente'}
                tono={s.kitchenPlanStatus === 'CONFIRMED' ? 'hecho' : 'espera'}
              />
            )}
          </div>
          <span className="sutil">Comercial: {s.owner.name}</span>
        </div>

        <Pase
          estado={s.status}
          planCocina={s.kitchenPlanStatus}
          enviada={s.sentAt}
          fechaFinal={s.finalStart}
          fechaFin={s.finalEnd}
          autoria={autoria}
        />

        <CintaEstado estado={s.status} timeline={s.timeline} />

        {fallo && <Aviso tono="error">{fallo}</Aviso>}

        {s.parent && (
          <Aviso tono="info">
            Esta solicitud nace de <Link to={`/solicitudes/${s.parent.id}`}>{s.parent.publicCode}</Link>.
          </Aviso>
        )}
        {s.children.length > 0 && (
          <Aviso tono="info">
            Se generó una nueva propuesta a partir de esta:{' '}
            {s.children.map((c) => (
              c.status === 'DRAFT' && !es('ADMIN') && s.owner.id !== usuario?.id
                ? <span key={c.id}>{c.publicCode} · pendiente del Comercial</span>
                : <Link key={c.id} to={`/solicitudes/${c.id}`} style={{ marginRight: 8 }}>
                    {c.publicCode}
                  </Link>
            ))}
          </Aviso>
        )}
        <div className="rejilla dos">
          <div className="pila">
            <BloqueComercial ficha={s} onGuardado={recargar} />
            <Franjas ficha={s} onCambio={recargar} />
            <BloqueJefe ficha={s} onGuardado={recargar} />
            <BloqueCocina ficha={s} onGuardado={recargar} />
            <PedidoMaterial ficha={s} onCambio={recargar} />
            <Valoracion ficha={s} onCambio={recargar} />
          </div>

          <div className="pila">
            <Tarjeta titulo="Cliente">
              <dl className="bloque" style={{ margin: 0 }}>
                <dt>Id. de cliente</dt>
                <dd className="dato">{s.client.externalId}</dd>
                <dt>Nombre comercial</dt>
                <dd>{s.client.commercialName}</dd>
                <dt>Población</dt>
                <dd>{[s.client.city, s.client.province].filter(Boolean).join(' · ') || '—'}</dd>
                <dt>Riesgo</dt>
                <dd className={s.client.riskDescription ? '' : 'sin-dato'}>
                  {s.client.riskDescription}
                </dd>
                <dt>Grupo</dt>
                <dd className={s.client.groupDescription ? '' : 'sin-dato'}>
                  {s.client.groupDescription}
                </dd>
              </dl>
            </Tarjeta>

            {es('ADMIN', 'SALES_MANAGER', 'COOK') && s.kitchenPlanStatus === 'CONFIRMED' && (
              <Correos requestId={s.id} />
            )}
          </div>
        </div>

        <div className="barra-acciones">
          {p.send && (
            <Boton
              variante="clave"
              onClick={() =>
                ejecutar(
                  () => api.post(`/requests/${s.id}/send`, { version: s.version }),
                  'Solicitud enviada al Jefe de Ventas.',
                )
              }
            >
              Enviar al Jefe de Ventas
            </Boton>
          )}
          {p.decide && s.status !== 'AUTHORIZED' && (
            <>
              <Boton variante="riesgo" onClick={() => setDialogo('rechazar')}>
                Rechazar
              </Boton>
              <Boton variante="clave" onClick={() => setDialogo('autorizar')}>
                Fijar fecha y autorizar
              </Boton>
            </>
          )}
        </div>
      </div>

      {dialogo === 'autorizar' && (
        <DialogoAutorizar
          ficha={s}
          onCerrar={() => setDialogo(null)}
          onHecho={() => {
            setDialogo(null);
            avisar('Visita autorizada. La Cocina ya puede preparar el plan.', 'hecho');
            recargar();
          }}
        />
      )}

      {historialAbierto && (
        <PanelHistorial
          timeline={s.timeline}
          codigo={s.publicCode}
          onCerrar={() => setHistorialAbierto(false)}
        />
      )}

      {(dialogo === 'rechazar' || dialogo === 'cancelar') && (
        <DialogoMotivo
          titulo={dialogo === 'rechazar' ? 'Rechazar la solicitud' : 'Cancelar la visita'}
          descripcion={
            dialogo === 'rechazar'
              ? 'El Comercial verá el motivo y podrá presentar una nueva propuesta.'
              : 'La visita se retira de la agenda y de los calendarios. El motivo queda en el histórico.'
          }
          etiqueta="Motivo"
          confirmar={dialogo === 'rechazar' ? 'Rechazar solicitud' : 'Cancelar visita'}
          onCerrar={() => setDialogo(null)}
          onConfirmar={(motivo) =>
            ejecutar(
              () =>
                api.post(`/requests/${s.id}/${dialogo === 'rechazar' ? 'reject' : 'cancel'}`, {
                  reason: motivo,
                  version: s.version,
                }),
              dialogo === 'rechazar' ? 'Solicitud rechazada.' : 'Visita cancelada.',
            )
          }
        />
      )}
    </>
  );
}

/* ─────────────────────────── Bloque del Comercial ───────────────────────── */

function BloqueComercial({ ficha, onGuardado }: { ficha: Ficha; onGuardado: () => void }) {
  const editable = Boolean(ficha.permissions?.editCommercial);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({
    visitType: ficha.visitType, venueType: ficha.venueType,
    venueOther: ficha.venueOther ?? '', productsToDiscuss: ficha.productsToDiscuss ?? '',
    reason: ficha.reason ?? '',
    products: ficha.products ?? '',
    tastingProposal: ficha.tastingProposal ?? '',
    commercialComments: ficha.commercialComments ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const { avisar } = useAvisos();

  async function guardar() {
    setGuardando(true);
    setFallo(null);
    try {
      await api.patch(`/requests/${ficha.id}/commercial-block`, { ...form, version: ficha.version });
      avisar('Datos del Comercial guardados.', 'hecho');
      setEditando(false);
      onGuardado();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Tarjeta
      titulo="Lo que pide el Comercial"
      extra={
        editable && !editando ? (
          <Boton variante="secundario" tam="s" onClick={() => setEditando(true)}>
            Editar
          </Boton>
        ) : null
      }
    >
      {fallo && <Aviso tono="error">{fallo}</Aviso>}

      {editando ? (
        <div className="pila">
          <DatosVisita valor={form} onCambio={(patch) => setForm((v) => ({ ...v, ...patch }))}
            localidad={[ficha.client.city, ficha.client.province].filter(Boolean).join(' · ')} />
          <Campo etiqueta="Motivo de la visita">
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Campo>
          {form.visitType !== 'NO_PRODUCT' && <Campo etiqueta="Productos que propone">
            <textarea
              value={form.products}
              onChange={(e) => setForm({ ...form, products: e.target.value })}
            />
          </Campo>}
          <Campo etiqueta="Propuesta de degustación">
            <textarea
              value={form.tastingProposal}
              onChange={(e) => setForm({ ...form, tastingProposal: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Comentarios para el Jefe de Ventas">
            <textarea
              value={form.commercialComments}
              onChange={(e) => setForm({ ...form, commercialComments: e.target.value })}
              style={{ minHeight: 120 }}
            />
          </Campo>
          <div className="fila" style={{ justifyContent: 'flex-end' }}>
            <Boton variante="secundario" onClick={() => setEditando(false)}>
              Descartar
            </Boton>
            <Boton variante="clave" cargando={guardando} onClick={guardar}>
              Guardar cambios
            </Boton>
          </div>
        </div>
      ) : (
        <dl className="bloque" style={{ margin: 0 }}>
          <dt>Tipo de visita</dt><dd>{tipoVisita(ficha.visitType)}</dd>
          <dt>Lugar</dt><dd>{lugarVisita(ficha.venueType, ficha.venueOther, ficha.client.city)}</dd>
          <dt>Motivo</dt>
          <dd className={ficha.reason ? '' : 'sin-dato'}>{ficha.reason}</dd>
          <dt>{ficha.visitType === 'NO_PRODUCT' ? 'Productos a comentar' : 'Productos que propone'}</dt>
          <dd>{ficha.visitType === 'NO_PRODUCT' ? ficha.productsToDiscuss : ficha.products}</dd>
          <dt>Propuesta de degustación</dt>
          <dd className={ficha.tastingProposal ? '' : 'sin-dato'}>{ficha.tastingProposal}</dd>
          <dt>Comentarios para el Jefe de Ventas</dt>
          <dd className={ficha.commercialComments ? '' : 'sin-dato'}>{ficha.commercialComments}</dd>
        </dl>
      )}
    </Tarjeta>
  );
}

/* ─────────────────────────── Fechas propuestas ──────────────────────────── */

function Franjas({ ficha, onCambio }: { ficha: Ficha; onCambio: () => void }) {
  const editable = Boolean(ficha.permissions?.editCommercial || ficha.permissions?.editManager);
  const [seleccion, setSeleccion] = useState<FranjaSel | null>(null);
  const inicio = seleccion?.inicio;
  const fin = seleccion?.fin;
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  async function anadir() {
    setFallo(null);
    setAviso(null);
    try {
      const r = await api.post<{ slot: Franja; warning: string | null }>(
        `/requests/${ficha.id}/slots`,
        { start: new Date(inicio!).toISOString(), end: new Date(fin!).toISOString() },
      );
      if (r.warning) setAviso(r.warning);
      setSeleccion(null);
      onCambio();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido añadir la fecha.');
    }
  }

  return (
    <Tarjeta titulo="Fechas propuestas">
      {fallo && <Aviso tono="error">{fallo}</Aviso>}
      {aviso && <Aviso tono="aviso">{aviso}</Aviso>}

      {ficha.slots.length === 0 ? (
        <p className="sutil" style={{ margin: 0 }}>
          Sin fechas propuestas.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
          {ficha.slots.map((f) => (
            <li key={f.id} className="fila" style={{ justifyContent: 'space-between' }}>
              <span className="dato">{franja(f.startAt, f.endAt)}</span>
              {editable && (
                <Boton
                  variante="plano"
                  tam="s"
                  onClick={async () => {
                    await api.del(`/requests/${ficha.id}/slots/${f.id}`);
                    onCambio();
                  }}
                >
                  Quitar
                </Boton>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="pila" style={{ marginTop: 16 }}>
          <SelectorFranja key={ficha.slots.length} bloquearOcupadas excludeRequestId={ficha.id} valor={seleccion} onCambio={setSeleccion} />
          <Boton variante="secundario" disabled={!inicio || !fin} onClick={anadir}>Añadir fecha</Boton>
        </div>
      )}
    </Tarjeta>
  );
}

/* ─────────────────────────── Bloque del Jefe ────────────────────────────── */

function BloqueJefe({ ficha, onGuardado }: { ficha: Ficha; onGuardado: () => void }) {
  const editable = Boolean(ficha.permissions?.editManager);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({
    managerAssessment: ficha.managerAssessment ?? '',
    managerPlan: ficha.managerPlan ?? '',
    managerComments: ficha.managerComments ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const { avisar } = useAvisos();

  if (ficha.status === 'DRAFT' && !editable) return null;

  async function guardar() {
    setGuardando(true);
    setFallo(null);
    try {
      await api.patch(`/requests/${ficha.id}/manager-block`, { ...form, version: ficha.version });
      avisar('Valoración guardada.', 'hecho');
      setEditando(false);
      onGuardado();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Tarjeta
      titulo="Valoración del Jefe de Ventas"
      extra={
        editable && !editando ? (
          <Boton variante="secundario" tam="s" onClick={() => setEditando(true)}>
            Editar
          </Boton>
        ) : null
      }
    >
      {fallo && <Aviso tono="error">{fallo}</Aviso>}

      {editando ? (
        <div className="pila">
          <Campo etiqueta="Valoración">
            <textarea
              value={form.managerAssessment}
              onChange={(e) => setForm({ ...form, managerAssessment: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Plan de la visita">
            <textarea
              value={form.managerPlan}
              onChange={(e) => setForm({ ...form, managerPlan: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Comentarios">
            <textarea
              value={form.managerComments}
              onChange={(e) => setForm({ ...form, managerComments: e.target.value })}
            />
          </Campo>
          <div className="fila" style={{ justifyContent: 'flex-end' }}>
            <Boton variante="secundario" onClick={() => setEditando(false)}>
              Descartar
            </Boton>
            <Boton variante="clave" cargando={guardando} onClick={guardar}>
              Guardar cambios
            </Boton>
          </div>
        </div>
      ) : (
        <dl className="bloque" style={{ margin: 0 }}>
          <dt>Fecha definitiva</dt>
          <dd className={ficha.finalStart ? 'dato' : 'sin-dato'}>
            {ficha.finalStart ? franja(ficha.finalStart, ficha.finalEnd) : ''}
          </dd>
          <dt>Valoración</dt>
          <dd className={ficha.managerAssessment ? '' : 'sin-dato'}>{ficha.managerAssessment}</dd>
          <dt>Plan de la visita</dt>
          <dd className={ficha.managerPlan ? '' : 'sin-dato'}>{ficha.managerPlan}</dd>
          <dt>Comentarios</dt>
          <dd className={ficha.managerComments ? '' : 'sin-dato'}>{ficha.managerComments}</dd>
        </dl>
      )}
    </Tarjeta>
  );
}

/* ─────────────────────────── Bloque de Cocina ───────────────────────────── */

function BloqueCocina({ ficha, onGuardado }: { ficha: Ficha; onGuardado: () => void }) {
  const editable = Boolean(ficha.permissions?.editKitchen);
  const plan = ficha.kitchenPlan;
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({
    plan: plan?.plan ?? '',
    tasks: plan?.tasks ?? '',
    materials: plan?.materials ?? '',
    comments: plan?.comments ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [reabriendo, setReabriendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const { avisar } = useAvisos();

  if (ficha.status !== 'AUTHORIZED' && !plan) return null;

  async function reabrir() {
    if (!plan) return;
    setReabriendo(true);
    setFallo(null);
    try {
      await api.post(`/kitchen/${ficha.id}/reopen`);
      avisar('Plan reabierto. Vuelve a estar en planificación y puedes modificarlo.', 'hecho');
      onGuardado();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido reabrir el plan.');
    } finally {
      setReabriendo(false);
    }
  }

  async function guardar(): Promise<PlanCocina | null> {
    if (!plan) return null;
    setGuardando(true);
    setFallo(null);
    try {
      const r = await api.patch<PlanCocina>(`/kitchen/${ficha.id}`, {
        ...form,
        version: plan.version,
      });
      avisar('Plan de cocina guardado.', 'hecho');
      setEditando(false);
      onGuardado();
      return r;
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar el plan.');
      return null;
    } finally {
      setGuardando(false);
    }
  }

  async function confirmar() {
    if (!plan) return;
    setConfirmando(true);
    setFallo(null);
    try {
      const actualizado = editando ? await guardar() : null;
      const version = actualizado?.version ?? plan.version;
      const r = await api.post<{ mail: { queued: number; recipients: string[] } }>(
        `/kitchen/${ficha.id}/confirm`,
        { version, notify: true },
      );
      avisar(
        r.mail.queued > 0
          ? `Plan confirmado. Se envían ${r.mail.queued} correos: ${r.mail.recipients.join(', ')}.`
          : 'Plan confirmado.',
        'hecho',
      );
      onGuardado();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido confirmar el plan.');
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <Tarjeta
      titulo="Plan de cocina"
      extra={
        <div className="fila">
          {plan && (
            <PildoraLibre
              texto={plan.status === 'CONFIRMED' ? 'Confirmado' : 'Pendiente'}
              tono={plan.status === 'CONFIRMED' ? 'hecho' : 'espera'}
            />
          )}
          {editable && !editando && (
            <Boton variante="secundario" tam="s" onClick={() => setEditando(true)}>
              Editar
            </Boton>
          )}
        </div>
      }
    >
      {fallo && <Aviso tono="error">{fallo}</Aviso>}

      {!plan ? (
        <p className="sutil" style={{ margin: 0 }}>
          Pendiente de autorización.
        </p>
      ) : editando ? (
        <div className="pila">
          <Campo etiqueta="Elaboración prevista">
            <textarea value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} />
          </Campo>
          <Campo etiqueta="Tareas previas">
            <textarea value={form.tasks} onChange={(e) => setForm({ ...form, tasks: e.target.value })} />
          </Campo>
          <Campo etiqueta="Material y géneros">
            <textarea
              value={form.materials}
              onChange={(e) => setForm({ ...form, materials: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Observaciones">
            <textarea
              value={form.comments}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
            />
          </Campo>
          <div className="fila" style={{ justifyContent: 'flex-end' }}>
            <Boton variante="secundario" onClick={() => setEditando(false)}>
              Descartar
            </Boton>
            <Boton variante="secundario" cargando={guardando} onClick={() => void guardar()}>
              Guardar sin confirmar
            </Boton>
            <Boton variante="clave" cargando={confirmando} onClick={confirmar}>
              {plan.status === 'CONFIRMED' ? 'Guardar y volver a avisar' : 'Confirmar y avisar'}
            </Boton>
          </div>
        </div>
      ) : (
        <>
          <dl className="bloque" style={{ margin: 0 }}>
            <dt>Elaboración prevista</dt>
            <dd className={plan.plan ? '' : 'sin-dato'}>{plan.plan}</dd>
            <dt>Tareas previas</dt>
            <dd className={plan.tasks ? '' : 'sin-dato'}>{plan.tasks}</dd>
            <dt>Material y géneros</dt>
            <dd className={plan.materials ? '' : 'sin-dato'}>{plan.materials}</dd>
            <dt>Observaciones</dt>
            <dd className={plan.comments ? '' : 'sin-dato'}>{plan.comments}</dd>
          </dl>
          {plan.confirmedAt && plan.status === 'CONFIRMED' && (
            <p className="sutil" style={{ margin: '12px 0 0' }}>
              Confirmado el {fechaHora(plan.confirmedAt)} · versión {plan.version}
            </p>
          )}
          {editable && plan.status === 'PENDING' && (
            <div className="fila" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <Boton variante="clave" cargando={confirmando} onClick={confirmar}>
                Confirmar y avisar
              </Boton>
            </div>
          )}
          {editable && plan.status === 'CONFIRMED' && (
            <div
              className="fila"
              style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}
            >
              <span className="sutil">
                ¿Quieres pensarlo de nuevo? Puedes reabrir el plan y volver a planificarlo.
              </span>
              <Boton variante="secundario" cargando={reabriendo} onClick={reabrir}>
                Reabrir plan
              </Boton>
            </div>
          )}
        </>
      )}
    </Tarjeta>
  );
}

/* ─────────────────────────── Correos enviados ───────────────────────────── */

function Correos({ requestId }: { requestId: string }) {
  const { datos } = useDatos(
    (s) =>
      api.get<{
        rows: Array<{
          id: string;
          recipient: string;
          type: string;
          status: string;
          planVersion: number;
          sentAt: string | null;
          lastError: string | null;
        }>;
      }>(`/kitchen/${requestId}/deliveries`, s),
    [requestId],
  );

  if (!datos?.rows.length) return null;

  return (
    <Tarjeta titulo="Correos de la visita">
      <table className="tabla">
        <tbody>
          {datos.rows.map((d) => (
            <tr key={d.id}>
              <td>
                <div style={{ fontWeight: 500 }}>{d.recipient}</div>
                <div className="sutil">Plan v{d.planVersion}</div>
              </td>
              <td style={{ textAlign: 'right' }}>
                <PildoraLibre
                  texto={
                    d.status === 'SENT' ? 'Enviado' : d.status === 'FAILED' ? 'Con error' : 'En cola'
                  }
                  tono={d.status === 'SENT' ? 'hecho' : d.status === 'FAILED' ? 'alto' : 'espera'}
                />
                <div className="sutil">{d.sentAt ? fechaHora(d.sentAt) : ''}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Tarjeta>
  );
}

/* ─────────────────────────── Diálogos ───────────────────────────────────── */

function DialogoAutorizar({
  ficha,
  onCerrar,
  onHecho,
}: {
  ficha: Ficha;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const primera = ficha.slots[0];
  // M-02 · el Jefe elige sobre la misma rejilla que usó el Comercial.
  const [franjaEl, setFranjaEl] = useState<FranjaSel | null>(() => {
    if (ficha.finalStart && ficha.finalEnd) {
      return { inicio: franjaATexto(new Date(ficha.finalStart)), fin: franjaATexto(new Date(ficha.finalEnd)) };
    }
    if (primera) {
      return { inicio: franjaATexto(new Date(primera.startAt)), fin: franjaATexto(new Date(primera.endAt)) };
    }
    return null;
  });
  const [comprobacion, setComprobacion] = useState<{ valid: boolean; message: string } | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const inicio = franjaEl?.inicio;
  const fin = franjaEl?.fin;
  const rangoValido = Boolean(inicio && fin && new Date(fin) > new Date(inicio));

  useEffect(() => {
    if (!rangoValido) {
      setComprobacion(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.post<{ valid: boolean; message: string }>('/requests/check-window', {
          start: new Date(inicio!).toISOString(),
          end: new Date(fin!).toISOString(),
          excludeRequestId: ficha.id,
        });
        setComprobacion(r);
      } catch {
        setComprobacion(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [inicio, fin, rangoValido, ficha.id]);

  async function autorizar() {
    setEnviando(true);
    setFallo(null);
    try {
      await api.post(`/requests/${ficha.id}/authorize`, {
        finalStart: new Date(inicio!).toISOString(),
        finalEnd: new Date(fin!).toISOString(),
        version: ficha.version,
      });
      onHecho();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido autorizar la visita.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialogo
      titulo="Fijar fecha y autorizar"
      descripcion="Sin fecha y hora definitivas la visita no puede autorizarse."
      onCerrar={onCerrar}
      acciones={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton
            variante="clave"
            cargando={enviando}
            disabled={!rangoValido || comprobacion?.valid === false}
            onClick={autorizar}
          >
            Autorizar visita
          </Boton>
        </>
      }
    >
      {fallo && <Aviso tono="error">{fallo}</Aviso>}

      {ficha.slots.length > 0 && (
        <div>
          <span className="eyebrow">Fechas propuestas por el Comercial</span>
          <div className="fila" style={{ marginTop: 8 }}>
            {ficha.slots.map((f) => (
              <Boton
                key={f.id}
                variante="secundario"
                tam="s"
                onClick={() =>
                  setFranjaEl({
                    inicio: franjaATexto(new Date(f.startAt)),
                    fin: franjaATexto(new Date(f.endAt)),
                  })
                }
              >
                {franja(f.startAt, f.endAt)}
              </Boton>
            ))}
          </div>
        </div>
      )}

      <SelectorFranja bloquearOcupadas excludeRequestId={ficha.id} valor={franjaEl} onCambio={setFranjaEl} />

      {comprobacion &&
        (comprobacion.valid ? (
          <Aviso tono="hecho">La franja está libre en la agenda.</Aviso>
        ) : (
          <Aviso tono="aviso">{comprobacion.message}</Aviso>
        ))}

      <Lectura etiqueta="Tipo de visita" valor={tipoVisita(ficha.visitType)} />
      <Lectura etiqueta="Lugar" valor={lugarVisita(ficha.venueType, ficha.venueOther, ficha.client.city)} />
      <Lectura etiqueta="Comentarios del Comercial" valor={ficha.commercialComments} />
    </Dialogo>
  );
}

function DialogoMotivo({
  titulo,
  descripcion,
  etiqueta,
  confirmar,
  onCerrar,
  onConfirmar,
}: {
  titulo: string;
  descripcion: string;
  etiqueta: string;
  confirmar: string;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Dialogo
      titulo={titulo}
      descripcion={descripcion}
      onCerrar={onCerrar}
      acciones={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton
            variante="riesgo"
            disabled={motivo.trim().length < 3}
            onClick={() => onConfirmar(motivo.trim())}
          >
            {confirmar}
          </Boton>
        </>
      }
    >
      <Campo etiqueta={etiqueta} ayuda="Queda registrado en el histórico y lo verá el Comercial.">
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      </Campo>
    </Dialogo>
  );
}

